import { Sparkles, BookOpen, HelpCircle, ChevronDown } from "lucide-react";

/**
 * V65 — AEO / GEO content primitives.
 *
 * Three additive components used by hub pages to strengthen visibility on
 * Google AI Overviews, ChatGPT Search, Gemini, Claude, Perplexity, Copilot,
 * DeepSeek, Mistral, xAI Grok, Meta AI, DuckAssist and all future LLMs:
 *
 *   1. <QuickAnswerCard>       — top-of-page "TL;DR" answer, 2-3 lines.
 *                                Matches the format AI answer engines prefer
 *                                to lift directly into their responses.
 *
 *   2. <DefinitionBox>         — inline "What is X?" definition + 4-6 line
 *                                explanation. Semantic-SEO friendly.
 *
 *   3. <ConversationalFAQ>     — expandable Q&A list in natural, conversational
 *                                tone. Different from the transactional /faqs
 *                                page — this is written for voice queries and
 *                                LLM ingestion (long-form answers, no bullets).
 *
 * All three are 100% additive — they inject nothing but semantic HTML + a small
 * amount of prose, so they never touch existing routes, forms, admin UI, DB
 * calls or business logic. Placed anywhere in the page tree, they degrade
 * gracefully on every viewport and preserve the current design language.
 *
 * Note: FAQPage JSON-LD is intentionally NOT emitted here — the SEO component
 * already handles that at the page level. This component is presentational
 * only, so the pages that use it can decide whether to also register the
 * schema (usually yes, for the FAQ list they render here).
 */

/* ------------------------------------------------------------------ */
/* 1) QuickAnswerCard                                                  */
/* ------------------------------------------------------------------ */

/**
 * A visually distinct answer card that AI models (ChatGPT, Gemini, Claude,
 * Perplexity) preferentially quote when generating an answer overview.
 *
 * Props
 *   question   – The user's likely search query, phrased as a question.
 *   answer     – The answer, 2-3 sentences, natural language, factual.
 *   bullets    – Optional string[] of 3-5 key points (helps AI extract).
 *   footer     – Optional accent line (e.g. "Founded 2013 · 50,000+ students").
 */
export function QuickAnswerCard({ question, answer, bullets, footer, testId }) {
    return (
        <section
            className="max-w-5xl mx-auto px-6 lg:px-8 -mt-8 sm:-mt-12 relative z-10"
            aria-labelledby="quick-answer-heading"
            data-testid={testId || "quick-answer-card"}
        >
            <div
                className="bg-white border-2 border-amber-200 rounded-2xl shadow-xl p-6 sm:p-8 relative overflow-hidden"
                data-speakable="true"
            >
                <div className="absolute -top-8 -right-8 w-32 h-32 bg-amber-100 rounded-full blur-2xl opacity-70" />
                <div className="relative">
                    <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-amber-50 border border-amber-200 text-[10px] font-bold uppercase tracking-[0.22em] text-amber-700 mb-3">
                        <Sparkles className="w-3 h-3" /> Quick Answer
                    </div>
                    <h2
                        id="quick-answer-heading"
                        className="font-heading text-lg sm:text-xl font-bold text-[#0F172A] leading-snug mb-3"
                    >
                        {question}
                    </h2>
                    <p className="text-[15px] sm:text-base text-slate-700 leading-relaxed">
                        {answer}
                    </p>
                    {Array.isArray(bullets) && bullets.length > 0 && (
                        <ul className="mt-4 grid sm:grid-cols-2 gap-x-6 gap-y-2 text-sm text-slate-700">
                            {bullets.map((b, i) => (
                                <li key={i} className="flex items-start gap-2 leading-relaxed">
                                    <span
                                        className="mt-1.5 w-1.5 h-1.5 rounded-full bg-[#1E3A8A] flex-shrink-0"
                                        aria-hidden="true"
                                    />
                                    <span>{b}</span>
                                </li>
                            ))}
                        </ul>
                    )}
                    {footer && (
                        <div className="mt-4 pt-4 border-t border-slate-100 text-xs text-slate-500 font-medium">
                            {footer}
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
}

/* ------------------------------------------------------------------ */
/* 2) DefinitionBox                                                    */
/* ------------------------------------------------------------------ */

/**
 * "What is X?" definition. Rendered as a small article-in-article block with
 * strong entity-first prose so AI models can lift the definition verbatim.
 *
 * Props
 *   term        – The entity being defined (e.g. "1:1 online tuition").
 *   definition  – 4-6 sentence explanation, natural language, no bullets.
 *   examples    – Optional string[] of concrete example use-cases.
 */
export function DefinitionBox({ term, definition, examples, testId }) {
    return (
        <section
            className="py-12 sm:py-16 bg-white"
            aria-labelledby={`define-${slugify(term)}-heading`}
            data-testid={testId || `definition-${slugify(term)}`}
        >
            <div className="max-w-4xl mx-auto px-6 lg:px-8">
                <div
                    className="border border-slate-200 rounded-2xl bg-[#F8FAFC] p-6 sm:p-8"
                    data-speakable="true"
                >
                    <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-[#1E3A8A]/5 border border-[#1E3A8A]/10 text-[10px] font-bold uppercase tracking-[0.22em] text-[#1E3A8A] mb-3">
                        <BookOpen className="w-3 h-3" /> Definition
                    </div>
                    <h2
                        id={`define-${slugify(term)}-heading`}
                        className="font-heading text-2xl sm:text-3xl font-bold text-[#0F172A] mb-4"
                    >
                        What is {term}?
                    </h2>
                    <p className="text-slate-700 leading-relaxed text-base sm:text-lg">
                        {definition}
                    </p>
                    {Array.isArray(examples) && examples.length > 0 && (
                        <div className="mt-5 pt-5 border-t border-slate-200">
                            <div className="text-[11px] font-bold uppercase tracking-[0.18em] text-slate-500 mb-2">
                                Typical scenarios
                            </div>
                            <ul className="space-y-1.5 text-sm text-slate-700 leading-relaxed">
                                {examples.map((e, i) => (
                                    <li key={i} className="flex items-start gap-2">
                                        <span className="mt-1.5 w-1.5 h-1.5 rounded-full bg-amber-500 flex-shrink-0" />
                                        <span>{e}</span>
                                    </li>
                                ))}
                            </ul>
                        </div>
                    )}
                </div>
            </div>
        </section>
    );
}

/* ------------------------------------------------------------------ */
/* 3) ConversationalFAQ                                                */
/* ------------------------------------------------------------------ */

/**
 * Long-form, natural-language Q&A block. Written for voice search and LLM
 * ingestion — full-sentence answers (60-140 words each), no bullets, no
 * keyword stuffing.
 *
 * Props
 *   heading    – Section heading (e.g. "Common questions parents ask us").
 *   subheading – Optional supporting one-liner.
 *   items      – Array of { q: string, a: string | JSX }.
 */
export function ConversationalFAQ({ heading, subheading, items, testId }) {
    if (!Array.isArray(items) || items.length === 0) return null;
    return (
        <section
            className="py-16 sm:py-20 bg-[#F8FAFC] border-y border-slate-100"
            aria-labelledby="conversational-faq-heading"
            data-testid={testId || "conversational-faq"}
        >
            <div className="max-w-4xl mx-auto px-6 lg:px-8">
                <div className="inline-flex items-center gap-1.5 px-3 py-1 rounded-full bg-white border border-slate-200 text-[10px] font-bold uppercase tracking-[0.22em] text-[#1E3A8A] mb-3">
                    <HelpCircle className="w-3 h-3" /> Answered clearly
                </div>
                <h2
                    id="conversational-faq-heading"
                    className="font-heading text-3xl sm:text-4xl font-bold text-[#0F172A] mb-3 leading-tight"
                >
                    {heading}
                </h2>
                {subheading && (
                    <p className="text-slate-600 text-base sm:text-lg mb-8 max-w-2xl">
                        {subheading}
                    </p>
                )}
                <div className="space-y-3" data-speakable="true">
                    {items.map(({ q, a }, i) => (
                        <details
                            key={i}
                            className="group bg-white border border-slate-200 rounded-xl p-5 sm:p-6 [&_summary::-webkit-details-marker]:hidden hover:border-[#1E3A8A]/40 transition-colors"
                            data-testid={`conv-faq-item-${i}`}
                        >
                            <summary className="cursor-pointer font-heading font-semibold text-[#0F172A] text-base sm:text-lg flex items-start justify-between gap-4 leading-snug">
                                <span>{q}</span>
                                <ChevronDown className="w-5 h-5 text-[#1E3A8A] shrink-0 mt-0.5 group-open:rotate-180 transition-transform" />
                            </summary>
                            <div className="mt-4 text-slate-700 leading-relaxed text-[15px] sm:text-base">
                                {a}
                            </div>
                        </details>
                    ))}
                </div>
            </div>
        </section>
    );
}

/* ------------------------------------------------------------------ */
/* Helpers                                                             */
/* ------------------------------------------------------------------ */

function slugify(s) {
    return String(s || "")
        .toLowerCase()
        .replace(/[^a-z0-9]+/g, "-")
        .replace(/^-|-$/g, "");
}

/**
 * Optional utility to build a FAQPage JSON-LD from a ConversationalFAQ item
 * array so pages can pass it through their existing <SEO schemas={...}>.
 */
export function buildConversationalFAQSchema(items) {
    if (!Array.isArray(items) || items.length === 0) return null;
    return {
        "@context": "https://schema.org",
        "@type": "FAQPage",
        "mainEntity": items.map(({ q, a }) => ({
            "@type": "Question",
            "name": q,
            "acceptedAnswer": {
                "@type": "Answer",
                "text": typeof a === "string" ? a : "",
            },
        })),
    };
}
